import { useState } from 'react';
import clsx from 'clsx';

/**
 * Design System · Avatar
 * Foto de perfil (avatarUrl da Pessoa) com fallback para iniciais.
 * Tooltip opcional com nome + papel (estilo Monday). Anel azul quando é o responsável atual.
 *
 * Usa apenas tokens Tailwind do tema (bg-panel, text-*, border-*), então adapta a claro/escuro.
 */

export type AvatarSize = 'sm' | 'md' | 'lg';

interface AvatarProps {
  nome: string;
  avatarUrl?: string;
  /** papel/etapa exibido no tooltip (ex.: "Edição"). Se ausente, tooltip mostra só o nome. */
  papel?: string;
  size?: AvatarSize;
  /** destaca com anel azul — usar no responsável da etapa atual. */
  ring?: boolean;
  /** mostra tooltip no hover. default true. */
  tooltip?: boolean;
  className?: string;
}

const SIZES: Record<AvatarSize, string> = {
  sm: 'w-6 h-6 text-[10px] border',
  md: 'w-9 h-9 text-xs border-2',
  lg: 'w-11 h-11 text-sm border-2',
};

export function iniciais(nome: string): string {
  const parts = nome.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

export function Avatar({ nome, avatarUrl, papel, size = 'md', ring, tooltip = true, className }: AvatarProps) {
  const [imgOk, setImgOk] = useState(true);
  const showImg = avatarUrl && imgOk;

  return (
    <span className={clsx('relative inline-block group/av', className)}>
      {showImg ? (
        <img
          src={avatarUrl}
          alt={nome}
          onError={() => setImgOk(false)}
          className={clsx(
            'rounded-full object-cover block border-panel',
            SIZES[size],
            ring && 'border-blue ring-2 ring-blue/40',
          )}
        />
      ) : (
        <span
          className={clsx(
            'rounded-full flex items-center justify-center font-extrabold text-white border-panel',
            'bg-gradient-to-br from-blue to-blue-apx',
            SIZES[size],
            ring && '!border-blue ring-2 ring-blue/40',
          )}
        >
          {iniciais(nome)}
        </span>
      )}

      {tooltip && (
        <span
          className={clsx(
            'pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-[calc(100%+8px)] z-30',
            'whitespace-nowrap rounded-md px-2 py-1 text-[11px] font-semibold',
            'bg-blue-deep text-white border border-border shadow-lg',
            'opacity-0 group-hover/av:opacity-100 transition-opacity',
          )}
        >
          {nome}
          {papel && <span className="text-blue-light font-bold"> · {papel}</span>}
        </span>
      )}
    </span>
  );
}
